import React, { useState, useCallback, useRef } from 'react';
import { X, Paintbrush, Loader2, Paperclip, Image as ImageIcon } from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import { inpaintRegion } from '../../services/slot-screen-generator/SlotScreenGeneratorService';
import { saveScreenResultImage } from '../../services/slot-screen-generator';
import type { SlotScreenId, ScreenResult } from '../../../core/types/slot-screen-generator.types';

interface RegionEditorProps {
  screenId: string;
  resultId: string;
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  onClose: () => void;
}

export function RegionEditor({ screenId, resultId, imageUrl, imageWidth, imageHeight, onClose }: RegionEditorProps) {
  const generationSettings = useSlotScreenGeneratorStore(s => s.generationSettings);
  const screen = useSlotScreenGeneratorStore(s => s.screens[screenId as SlotScreenId]);
  const effectiveModel = screen?.generationModel ?? generationSettings.model;

  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;
    const file = files[0];
    if (file?.type.startsWith('image/')) {
      if (referenceImage) URL.revokeObjectURL(referenceImage);
      setReferenceImage(URL.createObjectURL(file));
    }
  }, [referenceImage]);

  // Handle paste (Ctrl+V)
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          if (referenceImage) URL.revokeObjectURL(referenceImage);
          setReferenceImage(URL.createObjectURL(file));
        }
        break;
      }
    }
  }, [referenceImage]);

  const removeReference = useCallback(() => {
    if (referenceImage) URL.revokeObjectURL(referenceImage);
    setReferenceImage(null);
  }, [referenceImage]);

  const handleRegenerate = async () => {
    if (!prompt) return;
    setIsGenerating(true);
    const store = useSlotScreenGeneratorStore.getState();
    try {
      // Use a default region covering the full image
      const mask = { x: 0, y: 0, w: imageWidth || 720, h: imageHeight || 1280 };
      // Pass original image + reference image + prompt to Gemini
      const newUrl = await inpaintRegion(
        imageUrl, mask, prompt, effectiveModel,
        undefined, // signal
        referenceImage ?? undefined, // reference image
      );
      const newResult: ScreenResult = {
        id: Math.random().toString(36).substring(2, 11),
        imageUrl: newUrl,
        savedPath: '',
        status: 'ready',
        timestamp: Date.now(),
        prompt: `[Edit] ${prompt}`,
        model: effectiveModel,
        width: imageWidth,
        height: imageHeight,
      };
      // Add as a new variant (don't replace original — user can select or revert)
      store.addScreenResult(screenId as SlotScreenId, newResult);
      // Save edited image to project folder (fire-and-forget)
      saveScreenResultImage(screenId as SlotScreenId, newResult)
        .then((savedPath) => {
          if (savedPath) store.updateResultSavedPath(screenId as SlotScreenId, newResult.id, savedPath);
        })
        .catch(() => {});
      store.addToast({ id: Date.now().toString(), type: 'success', message: 'Edit added — click to select or keep original', timestamp: Date.now() });
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      store.addToast({ id: Date.now().toString(), type: 'error', message: `Edit failed: ${msg}`, timestamp: Date.now() });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="absolute inset-0 z-[60] bg-zinc-900/95 flex flex-col" onPaste={handlePaste}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-700 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Paintbrush size={16} className="text-blue-400" />
          <span className="text-sm font-medium">Edit Region</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-zinc-700 rounded text-zinc-400 hover:text-zinc-200">
          <X size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Full image display */}
        <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
          <img
            src={imageUrl}
            alt="Edit region"
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>

        {/* Right sidebar — reference image + prompt */}
        <div className="w-[280px] flex-shrink-0 border-l border-zinc-700 flex flex-col p-3 gap-3">
          {/* Reference image area */}
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Reference image (screenshot the part to change)</label>
            {referenceImage ? (
              <div className="relative rounded-lg overflow-hidden border border-zinc-700">
                <img src={referenceImage} alt="Reference" className="w-full h-auto max-h-40 object-contain bg-zinc-800" />
                <button
                  onClick={removeReference}
                  className="absolute top-1 right-1 p-0.5 bg-black/60 rounded hover:bg-red-900/80 transition-colors"
                >
                  <X size={12} className="text-zinc-300" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-2 py-6 border-2 border-dashed border-zinc-700 rounded-lg text-zinc-500 hover:border-zinc-600 hover:text-zinc-400 hover:bg-zinc-800/30 transition-colors"
              >
                <ImageIcon size={24} strokeWidth={1.5} />
                <span className="text-[11px]">Paste (Ctrl+V) or click to upload</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => { handleFileSelect(e.target.files); e.target.value = ''; }}
            />
          </div>

          {/* Prompt input */}
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Describe what to change</label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="e.g. 'change the WILD symbol to a dragon', 'make the background darker'..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-2 text-xs text-zinc-200 resize-y min-h-[80px] focus:outline-none focus:border-zinc-600"
              onPaste={handlePaste}
            />
          </div>

          {/* Generate button */}
          <button
            onClick={handleRegenerate}
            disabled={!prompt || isGenerating}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-xs font-medium px-3 py-2 rounded-lg transition-colors"
          >
            {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Paintbrush size={14} />}
            {isGenerating ? 'Regenerating...' : 'Regenerate Region'}
          </button>
        </div>
      </div>
    </div>
  );
}
